import { Content, ContentStack, ContentTable, ContentText, TDocumentDefinitions } from "pdfmake/interfaces";
import {
  HeaderLogoWithAddress
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import FormSymbolImages from "react-lib/apps/common/pdfPrintList/AddSymbolPrintList";

type FormConsentOrderNextTHProps = {
  detail: Record<string, any>; // -- Type จาก PrintListFormDataHelper
  items: Record<string, any>; // -- Type PatientInfo + additionData จาก PrintListFormDataHelper
};

const FONT_SIZE = 9.5;
const FORM_NAME = "FormConsentOrderNextTH";

const FormConsentOrderNextTH = async (
  props: FormConsentOrderNextTHProps,
): Promise<TDocumentDefinitions> => {

  const headerForm = await HeaderLogoWithAddress({
    font: "KanitLM",
    form: FORM_NAME,
    headerMargins: [10, 10, 10, 10],
    logoHeight: 50,
    pageMargins: [15, 70, 15, 30],
    oaDetail: props.detail?.oaDetail,
    division: props?.detail?.division,
    isFullAddress: !!props.detail?.oaDetail,
    isOrderPrintList: true,
    styles: {
      fieldHeader: { alignment: "center", bold: true, fontSize: 11, marginTop: 5 },
      fieldKey: { bold: true },
      fontEN: { font: "PoppinsLM", fontSize: 9.25 },
      tableHeader: { bold: true },

      // 
      
    },
    images: {
      oaLogo: props.detail?.oaDetail?.imageFile || "",
      ...FormSymbolImages,
    },
  });

  const { font, images, styles } = headerForm;

  const fillerValue = (value: string | undefined | null, dottedCount: number): ContentStack => ({
    stack: [
      {
        text: value || " ",
        preserveLeadingSpaces: true,
        alignment: "center",
        marginBottom: -10,
      },
      {
        text: ".".repeat(dottedCount),
      }
    ],
    width: "auto"
  });

  const formatPrice = (value: number): string =>
    value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const packageItems: Record<string, any>[] = props.items?.items || [];

  const packageGrandTotal = packageItems.reduce(
    (sum: number, item: Record<string, any>) => sum + (Number(item?.price) || 0),
    0,
  );

  const packagePurchaseListTable: ContentTable | null =
    packageItems.length === 0
      ? null
      : {
          table: {
            widths: ["80%", "20%"],
            body: [
              [
                { text: "รายการตรวจ", bold: true, alignment: "center" },
                { text: "ราคา", bold: true, alignment: "right" },
              ],
              ...packageItems.map((item: Record<string, any>) => [
                { text: `${item?.product_name || ""}`, preserveLeadingSpaces: true },
                { text: formatPrice(Number(item?.price) || 0), alignment: "right" },
              ]),
              [
                { text: "ค่าบริการรวม", bold: true, alignment: "center" },
                { text: formatPrice(packageGrandTotal), bold: true, alignment: "right" },
              ],
            ],
          },
          layout: {
            hLineWidth: (rowIndex: number, node: ContentTable) =>
              rowIndex === 0 ||
              rowIndex === 1 ||
              rowIndex === node.table.body.length - 1 ||
              rowIndex === node.table.body.length
                ? 1
                : 0,
            vLineWidth: () => 0,
          },
        } as ContentTable;

  return {
    defaultStyle: {
      font,
      fontSize: FONT_SIZE,
      lineHeight: 1,
    },
    pageSize: "A4",
    ...styles,
    ...headerForm,
    images: {
      ...images,
    },
    content: [
      {
        table: {
          widths: ["100%"],
          body: [
            [
              {
                text: "แบบยินยอมรับบริการตรวจทางห้องปฏิบัติการ",
                bold: true,
                color: "white",
                fillColor: "#333333",
                alignment: "center",
                fontSize: 11,
              }
            ],
            [
              {
                stack: [
                  {
                    columns: [
                      { text: "ชื่อ-นามสกุล : ", width: "auto" },
                      fillerValue(props.items?.full_name_th, 138),
                      { text: "Patient ID : ", width: "auto" },
                      fillerValue(props.detail?.patientHN, 30),
                      { text: "วันที่รับบริการ : ", width: "auto" },
                      fillerValue(props.items?.formatVisitDate, 65),
                    ],
                    columnGap: 5,
                    marginBottom: -12,
                  },
                  {
                    columns: [
                      { text: "เลขบัตรประชาชน / Passport No. : ", width: "auto" },
                      fillerValue(props.items?.formatCitizenNo, 100),
                      { text: "วันเดือนปีเกิด : ", width: "auto" },
                      fillerValue(props.items?.birthdate, 60),
                      { text: "อายุ : ", width: "auto" },
                      fillerValue(props.items?.age, 20),
                      { text: "ปี", width: "auto" },
                    ],
                    columnGap: 5,
                    marginBottom: -2,
                  },
                  {
                    columns: [
                      { text: "โทรศัพท์ : ", width: "auto" },
                      fillerValue(props.detail?.telMobile, 148),
                      { text: "อีเมล : ", width: "auto" },
                      fillerValue(props.items?.email, 148),
                    ],
                    columnGap: 5,
                    marginBottom: -10,
                  },
                  {
                    columns: [
                      { text: "| ผู้ติดต่อฉุกเฉิน |", bold: true, width: "auto" },
                      { text: "ชื่อ-นามสกุล : ", width: "auto" },
                      fillerValue(props.items?.relative_address?.owner_name, 118),
                      { text: "โทรศัพท์ : ", width: "auto" },
                      fillerValue(props.items?.relative_address?.tel_mobile, 50),
                      { text: "ความสัมพันธ์ : ", width: "auto" },
                      fillerValue(props.items?.relative_address?.relative, 30),
                    ],
                    columnGap: 5,
                    marginBottom: -10,
                  },
                  {
                    columns: [
                      { text: "|", bold: true, width: "auto" },
                      {
                        text: "กรณีผู้รับบริการเป็นผู้เยาว์ / ไม่สามารถให้ความยินยอมได้ด้วยตนเอง",
                        italics: true,
                        bold: true,
                        width: "auto"
                      },
                      { text: "|", bold: true, width: "auto" },
                    ],
                    columnGap: 5,
                    // marginBottom: 5,
                  },
                  {
                    columns: [
                      { text: "ชื่อ-นามสกุลผู้แทนโดยชอบธรรม / ผู้ใช้อำนาจปกครอง : ", noWrap: true, width: "auto" },
                      fillerValue(" ", 180),
                    ],
                    columnGap: 5,
                    marginBottom: -2,
                  },
                  {
                    columns: [
                      { text: "โทรศัพท์ : ", width: "auto" },
                      fillerValue(" ", 138),
                      { text: "ความสัมพันธ์ : ", width: "auto" },
                      fillerValue(" ", 140),
                    ],
                    columnGap: 5,
                    marginBottom: -10,
                  },
                ],
              }
            ]
          ]
        },
        layout: "noBorders",
        // marginBottom: 5,
      },

      // Package Purchase List
      ...(packagePurchaseListTable ? [packagePurchaseListTable as Content] : []),

      // ข้อตกลงการรับบริการตรวจทางห้องปฏิบัติการ
      {
        table: {
          headerRows: 1,
          widths: ["100%"],
          body: [
            [
              {
                text: "ข้อตกลงการรับบริการตรวจทางห้องปฏิบัติการ",
                bold: true,
                fillColor: "#9e9e9e",
                marginLeft: 5
              },
            ],
            [
              {
                ul: [
                  { text: `ข้าพเจ้ารับรองว่าข้อมูลสุขภาพ ประวัติการรักษา และประวัติแพ้ยา/สารเคมีที่แจ้งเป็นความจริงทุกประการหากปกปิด หรือ ให้ข้อมูลเท็จ คลินิกขอสงวน สิทธิ์ไม่รับผิดชอบต่อผลกระทบที่เกิดขึ้น` },
                  { text: `ข้าพเจ้ายินยอมให้นักเทคนิคการแพทย์ผู้มีใบอนุญาตเก็บตัวอย่างสิ่งส่งตรวจ (เลือด/ปัสสาวะ/สารคัดหลั่ง) ตามมาตรฐาน และ รับทราบว่า มีความเสี่ยง เล็กน้อย เช่น รอยฟกช้ำหรือเวียนศีรษะชั่วคราวซึ่งเป็นผลข้างเคียงปกติ` },
                  { text: `ข้าพเจ้าเข้าใจและยอมรับว่าผลการตรวจเป็นเพียงข้อมูลทางวิทยาศาสตร์ ไม่ใช่การวินิจฉัยหรือรักษาโรคโดยแพทย์ หากต้องการวินิจฉัย หรือรักษา ข้าพเจ้าจะพบแพทย์ผู้มีใบอนุญาตเพิ่มเติม` },
                  { text: `ค่าบริการที่ชำระแล้วไม่สามารถขอคืนได้เมื่อเก็บตัวอย่างสิ่งส่งตรวจแล้ว ยกเว้นกรณีคลินิกบกพร่องในการให้บริการ และหากต้องส่งตรวจห้องปฏิบัติ-การภายนอกอาจมีค่าใช้จ่ายเพิ่มเติมซึ่งจะแจ้งให้ทราบล่วงหน้า` },
                  { text: `ข้อปฏิบัติหลังรับบริการ: เจาะเลือด-กดแผลอย่างน้อย 3-5 นาที ห้ามนวด/ถูแขน หากบวมให้ประคบเย็นและพบแพทย์หากไม่ดีขึ้นใน 24ชม., ตรวจปัสสาวะ-เลี่ยงอาหาร/ยาที่กระทบผลตรวจ, ตรวจครรภ์-ผลต้องยืนยันจากสูตินรีแพทย์, หากมีอาการผิดปกติควรพบแพทย์ทันทีและแจ้งให้คลินิกทราบ` },
                  { text: `กรณีผู้รับบริการเป็นผู้เยาว์หรือไม่สามารถให้ความยินยอมได้ด้วยตนเอง ให้ผู้แทนโดยชอบธรรม ผู้ใช้อำนาจปกครอง หรือบุคคลผู้มีอำนาจตามกฎหมาย เป็นผู้ให้ความยินยอมแทนตามที่กฎหมายกำหนด` },
                ]
              }
            ]
          ]
        },
        layout: "noBorders",
      },

      // ช่องทางการรับผลการตรวจ
      {
        table: {
          headerRows: 1,
          widths: ["100%"],
          body: [
            [
              {
                text: [
                  { text: "ช่องทางการรับผลการตรวจ", bold: true },
                  { text: " (นอกเหนือจากสมุดสุขภาพในเมนูไลน์)", italics: true, preserveLeadingSpaces: true },
                ],
                fillColor: "#9e9e9e",
                marginLeft: 5
              }
            ],
            [
              {
                columns: [
                  { image: "unchecked", width: 10, marginTop: 2 },
                  { text: "ไลน์แชท", width: "auto" },
                  { image: "unchecked", width: 10, marginTop: 2 },
                  { text: "รับผลเอกสารที่คลินิก", width: "auto" },
                  { image: "unchecked", width: 10, marginTop: 2 },
                  { text: `อีเมล : ${".".repeat(226)}`, width: "auto" },
                ],
                columnGap: 5,
                // marginTop: 5,
              }
            ]
          ]
        },
        layout: "noBorders",
      },

      //
      {
        text: `ข้าพเจ้ารับทราบและยืนยันรายการตรวจตามที่ระบุในใบสั่งตรวจ และยินยอมเข้ารับบริการตรวจทางห้องปฏิบัติการตามรายการดังกล่าว รวมทั้งได้อ่าน และรับทราบเงื่อนไขการให้บริการตรวจทางห้องปฏิบัติการของคลินิก และตกลงผูกพันตามเงื่อนไขที่ระบุไว้ในเอกสารฉบับนี้ทุกประการ`,
        bold: true,
        marginBottom: 5,
      },
      
      //
      {
        stack: [
          {
            columns: [
              { text: " ", width: "*" },
              { text: "ลงชื่อผู้รับบริการ / ผู้แทนโดยชอบธรรม : ", width: "auto" },
              { text: ".".repeat(100), width: "auto" },
              { text: `วันที่${".".repeat(20)}/${".".repeat(20)}/${".".repeat(20)}`, width: "auto" },
            ],
            alignment: "right",
            columnGap: 5,
          },
          {
            columns: [
              { text: " ", width: "*" },
              { text: "ลงชื่อเจ้าหน้าที่ : ", width: "auto" },
              { text: ".".repeat(100), width: "auto" },
              { text: `วันที่${".".repeat(20)}/${".".repeat(20)}/${".".repeat(20)}`, width: "auto" },
            ],
            alignment: "right",
            columnGap: 5,
          }
        ],
      },
    ],
    footer: (currentPage: number, pageCount: number) => ({
      alignment: "right",
      marginRight: 15,
      text: `Page ${currentPage} of ${pageCount}`,
    }),
  }
};

export default FormConsentOrderNextTH;
